/**
 * SquadTvStandingRow — one dense Stadium-board row: rank, avatar, name, trend
 * arrow, and points. Top-3 get a medal accent bar and gold numerals.
 * Avatar rendering reuses the shared FriendAvatar primitive — the same path the
 * regular WorldCup leaderboard uses (avatarId → profile photo → initials) — so
 * the board matches the live app and depends on nothing avatar-customization-new.
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { SQUAD_TV_STADIUM_AVATAR_SIZE } from '../../../src/worldcup/constants/squadTvMode.constants';
import { getSquadTvStandingRowStyles } from './SquadTvStandingRow.styles';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';

export const SquadTvStandingRow: React.FC<{
  entry: LeaderboardEntry;
  /** Local position within the single (un-duplicated) column — drives zebra striping. */
  index: number;
}> = ({ entry, index }) => {
  const theme = useTheme();
  const styles = useMemo(() => getSquadTvStandingRowStyles(theme), [theme]);

  const isTop = entry.rank <= 3;
  const accentStyle =
    entry.rank === 1 ? styles.accentGold : entry.rank === 2 ? styles.accentSilver : styles.accentBronze;
  const rankColorStyle =
    entry.rank === 1 ? styles.rankGold : entry.rank === 2 ? styles.rankSilver : styles.rankBronze;
  const avatarId =
    entry.avatarId != null && entry.avatarId !== ''
      ? normalizeDisplayAvatarId(entry.avatarId)
      : undefined;

  return (
    <View
      style={[
        styles.row,
        index % 2 === 1 && styles.rowAlt,
        entry.isCurrentUser && styles.rowMe,
      ]}
    >
      {isTop ? <View style={[styles.topAccent, accentStyle]} /> : null}

      <View style={styles.rankCell}>
        <Text style={[styles.rankText, isTop && rankColorStyle]}>{entry.rank}</Text>
      </View>

      <View style={styles.who}>
        <FriendAvatar
          avatarId={avatarId}
          fallbackUrl={entry.avatarUrl ?? undefined}
          size={SQUAD_TV_STADIUM_AVATAR_SIZE}
          nameForInitials={entry.name}
          avatarRounded
        />
        <Text style={[styles.name, isTop && styles.nameTop]} numberOfLines={1}>
          {entry.name}
        </Text>
      </View>

      <View style={styles.pts}>
        <Text style={[styles.ptsValue, isTop && styles.ptsValueTop]}>
          {entry.points.toLocaleString('en-US')}
        </Text>
        <Text style={styles.ptsUnit}>pts</Text>
      </View>
    </View>
  );
};
